import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, todayStr } from "@/lib/api";
import { playRibbit, playSplash, playPop, isMuted, setMuted } from "@/lib/sounds";
import { BAND_UI } from "@/lib/bands";
import { FrogFace } from "@/components/FrogArt";
import { FrogPond } from "@/components/FrogPond";
import { FrogPicker } from "@/components/FrogPicker";
import { TadpolePicker } from "@/components/TadpolePicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Check, CloudSun, Crown, History, Lock, PartyPopper, RotateCw, ThumbsDown, ThumbsUp, Trophy, Volume2, VolumeX } from "lucide-react";

const TrophyShelf = ({ child }) => {
  const finished = [...(child.past_cycles || [])];
  if (child.stats.finished_program) finished.push({ cycle: child.cycle, stats: child.stats });
  if (finished.length === 0) return null;
  const best = Math.max(...finished.map((p) => p.stats.done_count));
  const inProgress = !child.stats.finished_program;
  const gap = best - child.stats.done_count;
  return (
    <div data-testid="trophy-shelf" className="bg-gradient-to-b from-amber-50 to-white rounded-3xl border-2 border-amber-200 p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Trophy className="w-5 h-5 text-amber-500" />
        <p className="font-display font-bold text-slate-800">Trophy Shelf</p>
      </div>
      <div className="flex flex-wrap gap-3">
        {finished.map((p) => {
          const isBest = p.stats.done_count === best;
          return (
            <div
              key={p.cycle}
              data-testid={`trophy-pond-${p.cycle}`}
              className={`relative rounded-2xl px-5 py-4 text-center border-2 ${isBest ? "bg-amber-100 border-amber-400" : "bg-white border-slate-200"}`}
            >
              {isBest && <Crown className="w-5 h-5 text-amber-500 absolute -top-2.5 left-1/2 -translate-x-1/2" fill="currentColor" />}
              <p className="font-display font-bold text-slate-800">Pond {p.cycle}</p>
              <p className={`font-display text-2xl font-bold ${isBest ? "text-amber-600" : "text-slate-600"}`}>{p.stats.done_count}<span className="text-sm text-slate-400">/14</span></p>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">frogs</p>
            </div>
          );
        })}
      </div>
      {inProgress && (
        <p data-testid="trophy-record-hint" className="text-sm font-semibold text-amber-700">
          {gap >= 0
            ? `Your record is ${best} frogs. Eat ${gap + 1} more this pond to beat it!`
            : `New record! ${child.stats.done_count} frogs and counting!`}
        </p>
      )}
    </div>
  );
};

const TadpoleList = ({ log, locked, onToggle }) => {
  if (!log.tadpoles?.length) return null;
  const doneCount = log.tadpoles.filter((t) => t.done).length;
  return (
    <div data-testid="tadpole-list" className="bg-sky-50 rounded-2xl p-5 border border-sky-100 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-sky-600 uppercase tracking-widest">Tadpoles {locked ? "— frog first!" : `(${doneCount}/${log.tadpoles.length})`}</p>
        {locked && <Lock className="w-4 h-4 text-sky-400" />}
      </div>
      {log.tadpoles.map((t) => (
        <button
          key={t.id}
          data-testid={`tadpole-check-${t.id}`}
          disabled={locked}
          onClick={() => onToggle(t.id)}
          className={`w-full flex items-center gap-3 rounded-xl p-3 text-left transition-colors ${
            locked ? "bg-white/60 opacity-60 cursor-not-allowed" : t.done ? "bg-green-100" : "bg-white hover:bg-sky-100"
          }`}
        >
          <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border-2 ${t.done ? "bg-green-500 border-green-500 text-white" : "border-slate-200 bg-white"}`}>
            {t.done && <Check className="w-4 h-4" />}
          </span>
          <span className={`font-semibold ${t.done ? "text-green-700 line-through" : "text-slate-700"}`}>{t.title}</span>
        </button>
      ))}
      {locked && <p className="text-xs text-sky-500">These unlock after you eat the frog. One thing at a time!</p>}
    </div>
  );
};

const Celebration = () => (
  <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
    {Array.from({ length: 24 }, (_, i) => (
      <motion.div
        key={i}
        initial={{ y: -40, x: `${Math.random() * 100}vw`, rotate: 0, opacity: 1 }}
        animate={{ y: "110vh", rotate: 360 * (Math.random() > 0.5 ? 1 : -1), opacity: 0.8 }}
        transition={{ duration: 2.2 + Math.random() * 1.5, ease: "easeIn" }}
        className="absolute w-3 h-3 rounded-sm"
        style={{ backgroundColor: ["#22C55E", "#FBBF24", "#38BDF8", "#F472B6", "#84CC16"][i % 5] }}
      />
    ))}
  </div>
);

export default function KidView() {
  const { childId } = useParams();
  const nav = useNavigate();
  const [child, setChild] = useState(null);
  const [bands, setBands] = useState(null);
  const [reflectOpen, setReflectOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [wasHard, setWasHard] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const [muted, setMutedState] = useState(isMuted());

  const toggleMute = () => {
    setMuted(!muted);
    setMutedState(!muted);
  };

  const load = useCallback(async () => {
    try {
      setChild(await api.getChild(childId));
    } catch {
      nav("/home");
    }
  }, [childId, nav]);

  useEffect(() => {
    load();
    api.getBands().then(setBands);
  }, [load]);

  if (!child || !bands) return <div className="min-h-screen bg-[#F0FDF4]" />;

  const ui = BAND_UI[child.age_band];
  const bandData = bands[child.age_band];
  const today = todayStr();
  const todayLog = child.logs.find((l) => l.date === today);
  const finished = child.stats.finished_program && !todayLog;

  const pickFrog = async (title, frogType, timeGuess, tadpoles) => {
    try {
      await api.setFrog(childId, { date: today, title, frog_type: frogType, time_guess: timeGuess, tadpoles });
      toast.success("Frog picked! Now go get it!");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't set the frog");
    }
  };

  const toggleTadpole = async (tpId) => {
    playPop();
    await api.toggleTadpole(todayLog.id, tpId);
    load();
  };

  const startNewCycle = async () => {
    try {
      await api.newCycle(childId);
      toast.success("Fresh pond! 14 new days start now.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start a new cycle");
    }
  };

  const answer = async (val) => {
    if (step === 1) {
      setWasHard(val);
      setStep(2);
    } else {
      await api.completeLog(todayLog.id, { was_hard: wasHard, started_first: val });
      setReflectOpen(false);
      setStep(1);
      playSplash();
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 3500);
      load();
    }
  };

  const doReset = async () => {
    await api.resetDay(childId, today);
    setResetOpen(false);
    toast("Easy day logged. Fresh start tomorrow.", { icon: "🌊" });
    load();
  };

  return (
    <div className="min-h-screen bg-[#F0FDF4]">
      {celebrate && <Celebration />}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-white/40 shadow-sm">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <Button data-testid="back-home-btn" variant="ghost" onClick={() => nav("/home")} className="rounded-xl font-semibold">
            <ArrowLeft className="w-4 h-4 mr-2" /> Pond
          </Button>
          <div className="flex items-center gap-2">
            <button
              data-testid="mute-toggle-btn"
              onClick={toggleMute}
              title={muted ? "Turn sounds on" : "Turn sounds off"}
              className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${muted ? "bg-slate-100 text-slate-400" : "bg-green-100 text-green-600"} hover:bg-green-200`}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center font-display font-bold text-white" style={{ backgroundColor: child.color }}>
              {child.name[0].toUpperCase()}
            </div>
            <span className="font-display font-bold text-slate-800">{child.name}</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        <div>
          <p className="text-sm font-bold text-green-600 uppercase tracking-widest">
            {child.cycle > 1 && `Pond ${child.cycle} • `}Day {todayLog ? todayLog.day_number : Math.min(child.stats.days_logged + 1, 14)} of 14 • {ui.label}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-800 mt-2 tracking-tight">{ui.kidTitle}</h1>
        </div>

        <AnimatePresence mode="wait">
          {finished ? (
            <motion.div key="finished" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-3xl p-10 border border-slate-100 shadow-lg text-center space-y-4">
              <Trophy className="w-16 h-16 text-yellow-400 mx-auto" />
              <h2 className="font-display text-2xl font-bold text-slate-800">14 days done. You're a frog-eater now!</h2>
              <p className="text-slate-500">Show a parent — this deserves a real celebration.</p>
              <Button
                data-testid="new-cycle-btn"
                onClick={startNewCycle}
                className="btn-tactile h-16 px-8 rounded-2xl bg-green-500 hover:bg-green-600 font-display font-bold text-lg"
              >
                <RotateCw className="w-5 h-5 mr-2" /> Start a fresh 14-day pond
              </Button>
              <p className="text-xs text-slate-400">Your finished pond stays saved below.</p>
            </motion.div>
          ) : !todayLog ? (
            <motion.div key="pick" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              {child.mode === "tadpole" ? (
                <TadpolePicker band={child.age_band} bandData={bandData} onPick={pickFrog} />
              ) : (
                <FrogPicker band={child.age_band} bandData={bandData} onPick={pickFrog} />
              )}
            </motion.div>
          ) : todayLog.status === "pending" ? (
            <motion.div key="working" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl p-8 sm:p-12 border border-slate-100 shadow-lg space-y-8 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-sky-100/60" />
              <div className="relative flex items-start gap-5">
                <motion.div animate={{ rotate: [0, -6, 6, 0] }} transition={{ repeat: Infinity, duration: 3 }}>
                  <FrogFace size={72} />
                </motion.div>
                <div>
                  <p className="text-sm font-bold text-sky-600 uppercase tracking-widest flex items-center gap-1.5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" /> Today's frog
                  </p>
                  <p data-testid="todays-frog-title" className="font-display text-2xl sm:text-3xl font-bold text-slate-800 mt-1">{todayLog.title}</p>
                  {todayLog.time_guess && <p className="text-sm text-sky-600 font-semibold mt-1">Your guess: {todayLog.time_guess} min</p>}
                  <p className="text-slate-500 mt-2">{ui.workingMsg}</p>
                </div>
              </div>
              <TadpoleList log={todayLog} locked onToggle={toggleTadpole} />
              <Button
                data-testid="mark-frog-done-btn"
                onClick={() => { playRibbit(); setReflectOpen(true); }}
                className="btn-tactile w-full h-20 rounded-3xl bg-gradient-to-br from-sky-500 via-cyan-500 to-blue-500 hover:brightness-110 text-white font-display font-bold text-2xl"
              >
                <PartyPopper className="w-7 h-7 mr-3" /> {ui.doneBtn}
              </Button>
              <button data-testid="easy-day-btn" onClick={() => setResetOpen(true)} className="w-full text-center text-sm text-slate-400 hover:text-sky-500 font-semibold transition-colors">
                Rough day? Log an Easy Day instead
              </button>
            </motion.div>
          ) : todayLog.status === "done" ? (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-b from-green-50 to-white rounded-3xl p-10 border-2 border-green-200 shadow-lg text-center space-y-4">
              <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="inline-block">
                <FrogFace size={90} />
              </motion.div>
              <h2 data-testid="frog-done-message" className="font-display text-3xl font-bold text-green-600">{ui.celebrate}</h2>
              <p className="text-slate-600 font-semibold">"{todayLog.title}" — eaten. {todayLog.tadpoles?.length ? "Now sweep up the tadpoles!" : "See you tomorrow!"}</p>
              <div className="text-left">
                <TadpoleList log={todayLog} locked={false} onToggle={toggleTadpole} />
              </div>
              {todayLog.tadpoles?.length > 0 && todayLog.tadpoles.every((t) => t.done) && (
                <p data-testid="all-tadpoles-done" className="font-display font-bold text-sky-600">Pond cleared — every tadpole checked off!</p>
              )}
            </motion.div>
          ) : (
            <motion.div key="reset" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-sky-50 rounded-3xl p-10 border-2 border-sky-200 text-center space-y-3">
              <CloudSun className="w-14 h-14 text-sky-400 mx-auto" />
              <h2 data-testid="easy-day-message" className="font-display text-2xl font-bold text-sky-700">{ui.resetMsg}</h2>
              <p className="text-slate-500">The streak bends — it doesn't break.</p>
            </motion.div>
          )}
        </AnimatePresence>

        <FrogPond logs={child.logs} />

        <TrophyShelf child={child} />

        {child.past_cycles?.length > 0 && (
          <section data-testid="past-ponds-section" className="space-y-4">
            <div className="flex items-center gap-2 text-slate-500">
              <History className="w-4 h-4" />
              <p className="font-display font-semibold text-sm uppercase tracking-widest">Past ponds</p>
            </div>
            {[...child.past_cycles].reverse().map((pc) => (
              <div key={pc.cycle} data-testid={`past-pond-${pc.cycle}`} className="space-y-2 opacity-90">
                <p className="text-sm font-bold text-slate-600">
                  Pond {pc.cycle} — {pc.stats.done_count}/14 frogs eaten{pc.stats.reset_count > 0 && `, ${pc.stats.reset_count} easy day${pc.stats.reset_count === 1 ? "" : "s"}`}
                </p>
                <FrogPond logs={pc.logs} />
              </div>
            ))}
          </section>
        )}
      </main>

      <Dialog open={reflectOpen} onOpenChange={(o) => { setReflectOpen(o); if (!o) setStep(1); }}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-center">{step === 1 ? ui.q1 : ui.q2}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 pt-4">
            <Button data-testid={`reflect-yes-btn`} onClick={() => answer(true)} className="btn-tactile h-24 rounded-3xl bg-green-500 hover:bg-green-600 font-display font-bold text-xl flex-col gap-1">
              <ThumbsUp className="w-8 h-8" /> Yes!
            </Button>
            <Button data-testid={`reflect-no-btn`} onClick={() => answer(false)} className="btn-tactile h-24 rounded-3xl bg-slate-200 hover:bg-slate-300 text-slate-700 font-display font-bold text-xl flex-col gap-1">
              <ThumbsDown className="w-8 h-8" /> Nope
            </Button>
          </div>
          <p className="text-center text-xs text-slate-400 mt-2">Question {step} of 2 — honest answers only, no wrong ones!</p>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Easy Day</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-sky-50 rounded-2xl p-5 border border-sky-100">
              <p className="text-xs font-bold text-sky-600 uppercase tracking-widest mb-2">Parent script — say this calmly</p>
              <p className="text-slate-700 text-sm leading-relaxed">{bandData.parent_script[3]}</p>
            </div>
            <p className="text-sm text-slate-500">An Easy Day still counts as a day. The streak bends, it doesn't break. No fight, no lecture.</p>
            <Button data-testid="confirm-easy-day-btn" onClick={doReset} className="btn-tactile w-full h-14 rounded-xl bg-sky-500 hover:bg-sky-600 font-display font-semibold text-lg">
              Log Easy Day
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
